/**
 * Types of Triggers based on triggering event types
 *  with helpers/templates for configuration strings
 */

import {
  EventTriggeringConditionType,
  TriggerScopeType,
} from '../../../generated/graphql';
import {OptionType} from './TriggersTable/utilTypes';

export {EventTriggeringConditionType} from '../../../generated/graphql';

export type EventTypeMetadata = {
  text: string;
  defaultScopeType: TriggerScopeType;
  templateStrings: TemplateValue[];
};

export type TemplateValue = {
  value: string;
  help: string;
};

export const eventTypeTemplateValue: TemplateValue[] = [
  {
    value: '${event_type}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the type of event that triggered the action.',
  },
];

// Only relevant to webhook payloads for now
export const eventTemplateValues: TemplateValue[] = [
  ...eventTypeTemplateValue,
  {
    value: '${event_author}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the user that triggered the action.',
  },
];

const artifactEventTemplateValues: TemplateValue[] = [
  {
    value: '${artifact_version}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the specific artifact version that triggered the action. Passed as an artifact instance.',
  },
  {
    value: '${artifact_version_string}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the specific artifact version that triggered the action. Passed as a string.',
  },
  {
    value: '${artifact_collection_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the artifact collection that the artifact version is linked to.',
  },
  {
    value: '${artifact_metadata.KEY}', // eslint-disable-line no-template-curly-in-string
    help:
      'Refers to a custom metadata value from the artifact version that triggered the action.  ' +
      'Replace "KEY" with the name of the actual key you wish to use.  ' +
      'Please note: only top-level metadata keys are currently permitted.',
  },
];

const aliasEventTemplateValues: TemplateValue[] = [
  {
    value: '${alias}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the alias that is being updated.',
  },
];

const tagEventTemplateValues: TemplateValue[] = [
  {
    value: '${tag}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the tag that is being updated.',
  },
];

const projectEventTemplateValues: TemplateValue[] = [
  {
    value: '${project_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the project owning the mutation that triggered the action.',
  },
];

const entityEventTemplateValues: TemplateValue[] = [
  {
    value: '${entity_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the entity owning the mutation that triggered the action.',
  },
];

const runEventTemplateValues: TemplateValue[] = [
  {
    value: '${run_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the run that triggered the action.',
  },
];

const metricEventTemplateValues: TemplateValue[] = [
  {
    value: '${metric_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the metric that triggered the action.',
  },
  {
    value: '${metric_values}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the series of values of the metric that triggered the action.',
  },
];

export const AddAliasEventMetadata: EventTypeMetadata = {
  text: 'An artifact alias is added',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...eventTemplateValues,
    ...aliasEventTemplateValues,
    ...artifactEventTemplateValues,
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
  ],
};

export const CreateArtifactEventMetadata: EventTypeMetadata = {
  text: 'A new version is added to an artifact',
  defaultScopeType: TriggerScopeType.ArtifactCollection,
  templateStrings: [
    ...eventTemplateValues,
    ...artifactEventTemplateValues,
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
  ],
};

export const UpdateAliasEventMetadata: EventTypeMetadata = {
  text: 'An artifact alias is updated',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...eventTemplateValues,
    ...aliasEventTemplateValues,
    ...artifactEventTemplateValues,
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
  ],
};

export const LinkModelEventMetadata: EventTypeMetadata = {
  text: 'A new version is linked to a collection',
  defaultScopeType: TriggerScopeType.ArtifactCollection,
  templateStrings: [
    ...eventTemplateValues,
    ...artifactEventTemplateValues,
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
  ],
};

export const AddArtifactTagEventMetadata: EventTypeMetadata = {
  text: 'An artifact tag is added',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...eventTemplateValues,
    ...tagEventTemplateValues,
    ...artifactEventTemplateValues,
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
  ],
};

export const RunMetricEventMetadata: EventTypeMetadata = {
  text: 'Run metrics threshold met',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
    ...eventTypeTemplateValue,
    ...runEventTemplateValues,
    ...metricEventTemplateValues,
  ],
};

export const RunMetricChangeEventMetadata: EventTypeMetadata = {
  text: 'Run metrics change threshold met',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
    ...eventTypeTemplateValue,
    ...runEventTemplateValues,
    ...metricEventTemplateValues,
  ],
};

export const RunStateEventMetadata: EventTypeMetadata = {
  text: 'Run status change',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...eventTypeTemplateValue,
    ...runEventTemplateValues,
    {
      value: '${run_status}', // eslint-disable-line no-template-curly-in-string
      help: 'Refers to the status of the run that triggered the action.',
    },
  ],
};

// TODO: Implement this
export const RunMetricZscoreEventMetadata: EventTypeMetadata = {
  text: 'Run metrics z-score threshold met',
  defaultScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...projectEventTemplateValues,
    ...entityEventTemplateValues,
    ...eventTypeTemplateValue,
    ...runEventTemplateValues,
    ...metricEventTemplateValues,
  ],
};

export const EventTypeToMetadata: {
  [key in EventTriggeringConditionType]: EventTypeMetadata;
} = {
  [EventTriggeringConditionType.AddArtifactAlias]: AddAliasEventMetadata,
  [EventTriggeringConditionType.CreateArtifact]: CreateArtifactEventMetadata,
  [EventTriggeringConditionType.LinkModel]: LinkModelEventMetadata,
  [EventTriggeringConditionType.UpdateArtifactAlias]: UpdateAliasEventMetadata,
  [EventTriggeringConditionType.AddArtifactTag]: AddArtifactTagEventMetadata,
  [EventTriggeringConditionType.RunMetric]: RunMetricEventMetadata,
  [EventTriggeringConditionType.RunMetricChange]: RunMetricChangeEventMetadata,
  [EventTriggeringConditionType.RunState]: RunStateEventMetadata,
  [EventTriggeringConditionType.RunMetricZscore]: RunMetricZscoreEventMetadata,
};

export const makeTriggerOptionForSelect = (
  eventType: EventTriggeringConditionType
): OptionType => {
  return {
    label: EventTypeToMetadata[eventType].text,
    value: eventType,
  };
};

export const allKnownEventTypes = Object.keys(
  EventTypeToMetadata
) as EventTriggeringConditionType[];
